/*!
 * MapView - Vue cartographique géographique interactive avec marqueurs et tuiles OpenStreetMap
 */

import CardView from './CardView';

export default class MapView extends CardView {
    constructor(plugin) {
        super(plugin, 'map');
        this.markers = [];
        this.bounds = { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 };
        this.mapCenter = { lat: 46.603354, lng: 1.888334, zoom: 6 }; // Centre France par défaut
        this.osmZoom = 6;
    }

    createContainer(element) {
        const myself = this;
        this.markers = [];
        this.bounds = { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 };

        this.container = $(`
            <div class="iv__map-layout">
                <div class="iv__map-viewport">
                    <div class="iv__map-canvas">
                        <iframe class="iv__map-osm-frame" frameborder="0" scrolling="no" marginheight="0" marginwidth="0"
                            src="about:blank"></iframe>
                        <div class="iv__map-markers-layer"></div>
                    </div>
                    <div class="iv__map-controls">
                        <button class="iv__map-btn iv__map-btn--zoom-in" title="${__('Zoomer')}"><i class="fa fa-plus"></i></button>
                        <button class="iv__map-btn iv__map-btn--zoom-out" title="${__('Dézoomer')}"><i class="fa fa-minus"></i></button>
                        <button class="iv__map-btn iv__map-btn--fit" title="${__('Ajuster aux points')}"><i class="fa fa-expand"></i></button>
                    </div>
                </div>
                <div class="iv__map-sidebar">
                    <div class="iv__map-sidebar__header">
                        <span class="iv__map-sidebar__title">${__('Éléments localisés')}</span>
                        <span class="iv__map-sidebar__count badge">0</span>
                    </div>
                    <div class="iv__map-sidebar__content iv__container"></div>
                </div>
            </div>
        `);

        myself.container = this.container.find('.iv__map-sidebar__content');
        myself.containerView.addClass('iv__view iv__view--map view view-map');
        element.append(this.container);

        // Liaison des contrôles de zoom
        this.bindMapControls();

        if (myself.preview) {
            element.append(myself.preview);
        }

        return myself.container;
    }

    /**
     * Contrôles de la carte
     */
    bindMapControls() {
        const $viewport = this.container.closest('.iv__map-layout').find('.iv__map-viewport');
        $viewport.find('.iv__map-btn--zoom-in').on('click', () => {
            this.osmZoom = Math.min(18, this.osmZoom + 1);
            this.refreshMapFrame();
        });
        $viewport.find('.iv__map-btn--zoom-out').on('click', () => {
            this.osmZoom = Math.max(2, this.osmZoom - 1);
            this.refreshMapFrame();
        });
        $viewport.find('.iv__map-btn--fit').on('click', () => {
            this.fitToBounds();
        });
    }

    /**
     * Extrait les coordonnées GPS d'un élément
     * @param {Object} data
     * @returns {{ lat: number, lng: number }|null}
     */
    extractCoordinates(data) {
        const raw = (data && data.data) ? data.data : data;
        let lat = raw.lat ?? raw.latitude ?? raw.geo_lat;
        let lng = raw.lng ?? raw.lon ?? raw.longitude ?? raw.geo_lng;

        if (lat === undefined && raw.coords) {
            if (Array.isArray(raw.coords)) {
                lat = raw.coords[0];
                lng = raw.coords[1];
            } else if (typeof raw.coords === 'string') {
                const parts = raw.coords.split(',');
                if (parts.length >= 2) {
                    lat = parseFloat(parts[0].trim());
                    lng = parseFloat(parts[1].trim());
                }
            }
        }

        if (lat !== undefined && lng !== undefined) {
            const pLat = parseFloat(lat);
            const pLng = parseFloat(lng);
            if (!isNaN(pLat) && !isNaN(pLng) && pLat >= -90 && pLat <= 90 && pLng >= -180 && pLng <= 180) {
                return { lat: pLat, lng: pLng };
            }
        }
        return null;
    }

    renderIcon(data, container, subdir, path) {
        const myself = this;
        if (data.type === 'root') return true;

        myself.viewItemThumbsMode = false;
        const currentType = this.getTypes(data);
        const currentClassAll = this.buildItemClasses(data, currentType, (data.class || data.cls || ''));

        const mapFields = myself.getViewFields('map') || myself.getViewFields('mosaic');
        const codeSubtext = this.buildFieldsSubtext(data, mapFields);
        const mapIconSize = myself.getViewIconSize('map') || 36;

        const coords = this.extractCoordinates(data);
        const hasCoords = coords !== null;

        const coordBadge = hasCoords
            ? `<span class="iv__map-item-coord" title="${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}"><i class="fa fa-map-marker"></i></span>`
            : `<span class="iv__map-item-coord iv__map-item-coord--none" title="${__('Non géolocalisé')}"><i class="fa fa-map-marker-slash"></i></span>`;

        const code = `
            <div class="${currentClassAll} iv__map-card" data-has-coords="${hasCoords ? '1' : '0'}">
                <div class="iv__item__frame">
                    <span class="iv__item__link">
                        <span class="iv__item__image">${myself.renderIconImage(mapIconSize, data.icon, data, 'img')}</span>
                        <label class="iv__item__label">
                            <span class="iv__item__name titre">${data.label || ''}</span>
                            ${coordBadge}
                            ${codeSubtext.length > 0 ? '<span class="iv__item__data infos">' + codeSubtext.join('') + '</span>' : ''}
                        </label>
                    </span>
                </div>
            </div>
        `;

        const currentIcon = $(code);
        const $sidebarContent = this.container.hasClass('iv__map-sidebar__content')
            ? this.container
            : this.container.closest('.iv__map-layout').find('.iv__map-sidebar__content');

        $sidebarContent.append(currentIcon);
        this.attachItemEvents(currentIcon, currentIcon, data, currentType, path);
        this.renderBadge(data, currentIcon);

        // Enregistrement du point sur la carte si coordonnées disponibles
        if (hasCoords) {
            this.registerMapMarker(data, coords, currentIcon);
        }

        // Mise à jour compteur du volet
        const total = $sidebarContent.find('.iv__map-card').length;
        this.container.closest('.iv__map-layout').find('.iv__map-sidebar__count').text(total);

        return currentIcon;
    }

    /**
     * Enregistre et place un marqueur sur la carte
     * @param {Object} data
     * @param {{ lat: number, lng: number }} coords
     * @param {jQuery} $card
     */
    registerMapMarker(data, coords, $card) {
        this.markers.push({ data, coords, $card });

        // Ajustement des bornes englobantes
        this.bounds.minLat = Math.min(this.bounds.minLat, coords.lat);
        this.bounds.maxLat = Math.max(this.bounds.maxLat, coords.lat);
        this.bounds.minLng = Math.min(this.bounds.minLng, coords.lng);
        this.bounds.maxLng = Math.max(this.bounds.maxLng, coords.lng);

        // Clic sur la carte dans la barre latérale pour centrer le marqueur
        $card.on('click', () => {
            this.centerOnMarker(coords);
        });

        // Mise à jour de l'iframe OpenStreetMap
        this.refreshMapFrame();
    }

    /**
     * Centre la carte sur des coordonnées
     * @param {{ lat: number, lng: number }} coords
     */
    centerOnMarker(coords) {
        this.mapCenter = { lat: coords.lat, lng: coords.lng, zoom: 15 };
        this.osmZoom = 15;
        this.refreshMapFrame();
    }

    /**
     * Ajuste la vue cartographique aux bornes de l'ensemble des points
     */
    fitToBounds() {
        if (!this.markers.length) return;
        const centerLat = (this.bounds.minLat + this.bounds.maxLat) / 2;
        const centerLng = (this.bounds.minLng + this.bounds.maxLng) / 2;
        this.mapCenter = { lat: centerLat, lng: centerLng, zoom: 12 };
        this.osmZoom = 12;
        this.refreshMapFrame();
    }

    /**
     * Recharge la carte OpenStreetMap avec les coordonnées courantes
     */
    refreshMapFrame() {
        if (!this.markers.length) return;

        let centerLat = this.mapCenter.lat;
        let centerLng = this.mapCenter.lng;

        if (this.bounds.minLat <= this.bounds.maxLat) {
            centerLat = (this.bounds.minLat + this.bounds.maxLat) / 2;
            centerLng = (this.bounds.minLng + this.bounds.maxLng) / 2;
        }

        const delta = 0.05 * Math.pow(2, 12 - this.osmZoom);
        const bbox = [
            (centerLng - delta).toFixed(5),
            (centerLat - delta).toFixed(5),
            (centerLng + delta).toFixed(5),
            (centerLat + delta).toFixed(5),
        ].join('%2C');

        const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${centerLat.toFixed(5)}%2C${centerLng.toFixed(5)}`;

        const $frame = this.container.closest('.iv__map-layout').find('.iv__map-osm-frame');
        if ($frame.length && $frame.attr('src') !== osmUrl) {
            $frame.attr('src', osmUrl);
        }
    }
}
